/**
 * Utilidades de validación reutilizables
 */

/**
 * Valida si un email tiene un formato válido
 */
export function isValidEmail(email: string): boolean {
  if (!email || typeof email !== 'string') {
    return false;
  }
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
}

/**
 * Valida si una contraseña cumple con los requisitos mínimos
 * @param password - Contraseña a validar
 * @param minLength - Longitud mínima (por defecto 8 para cumplir con Supabase)
 */
export function isValidPassword(password: string, minLength: number = 8): boolean {
  if (!password || typeof password !== 'string') {
    return false;
  }
  return password.trim().length >= minLength;
}

/**
 * Valida si un campo de texto no está vacío
 */
export function isNotEmpty(value: string): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * Valida si una fecha es válida y no es futura
 */
export function isValidDate(date: string | Date): boolean {
  if (!date) return false;
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) {
    return false;
  }
  
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  
  return dateObj <= today;
}

/**
 * Valida si una fecha está en un rango válido
 */
export function isDateInRange(
  date: string | Date,
  minDate?: Date,
  maxDate?: Date
): boolean {
  if (!date) return false;
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) {
    return false;
  }
  
  if (minDate && dateObj < minDate) {
    return false;
  }
  
  if (maxDate && dateObj > maxDate) {
    return false;
  }
  
  return true;
}

/**
 * Resultado de validación con mensaje de error opcional
 */
export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Valida un email y retorna resultado con mensaje de error
 */
export function validateEmail(email: string): ValidationResult {
  if (!isNotEmpty(email)) {
    return { isValid: false, error: 'El email es requerido' };
  }
  
  if (!isValidEmail(email)) {
    return { isValid: false, error: 'Por favor ingresa un email válido' };
  }
  
  return { isValid: true };
}

/**
 * Valida una contraseña y retorna resultado con mensaje de error
 * Valida según los requisitos de Supabase: mínimo 8 caracteres recomendado
 */
export function validatePassword(
  password: string,
  minLength: number = 8
): ValidationResult {
  if (!isNotEmpty(password)) {
    return { isValid: false, error: 'La contraseña es requerida' };
  }
  
  const trimmedPassword = password.trim();
  
  if (trimmedPassword.length < minLength) {
    return {
      isValid: false,
      error: `La contraseña debe tener al menos ${minLength} caracteres`,
    };
  }
  
  // Alinear reglas con la política estricta de contraseña de Supabase:
  // al menos 1 minúscula, 1 mayúscula, 1 número y 1 símbolo.
  const hasLowercase = /[a-z]/.test(trimmedPassword);
  const hasUppercase = /[A-Z]/.test(trimmedPassword);
  const hasNumber = /[0-9]/.test(trimmedPassword);
  const hasSymbol = /[^A-Za-z0-9]/.test(trimmedPassword);

  if (!hasLowercase || !hasUppercase || !hasNumber || !hasSymbol) {
    return {
      isValid: false,
      error: 'La contraseña debe incluir al menos una minúscula, una mayúscula, un número y un símbolo',
    };
  }
  
  return { isValid: true };
}

/**
 * Valida que dos contraseñas coincidan
 */
export function validatePasswordMatch(
  password: string,
  confirmPassword: string
): ValidationResult {
  if (!isNotEmpty(confirmPassword)) {
    return { isValid: false, error: 'Por favor confirma tu contraseña' };
  }
  
  if (password.trim() !== confirmPassword.trim()) {
    return { isValid: false, error: 'Las contraseñas no coinciden' };
  }
  
  return { isValid: true };
}

/**
 * Valida una fecha de nacimiento
 */
export function validateBirthDate(date: string): ValidationResult {
  if (!date) {
    return { isValid: true }; // Fecha opcional
  }
  
  const minDate = new Date('1900-01-01');
  const maxDate = new Date();
  
  if (!isDateInRange(date, minDate, maxDate)) {
    if (!isValidDate(date)) {
      return { isValid: false, error: 'Por favor ingresa una fecha de nacimiento válida' };
    }
    const dateObj = new Date(date);
    if (dateObj > maxDate) {
      return { isValid: false, error: 'La fecha de nacimiento no puede ser futura' };
    }
    if (dateObj < minDate) {
      return { isValid: false, error: 'La fecha de nacimiento no es válida' };
    }
  }
  
  return { isValid: true };
}
